import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, 
  Home, 
  Briefcase, 
  Building2, 
  Plus, 
  Check, 
  X, 
  User, 
  ChevronRight, 
  Trash2, 
  Loader2,
  ArrowLeft,
  Crosshair,
  Navigation
} from 'lucide-react';
import { lookupCustomerAddresses } from '../../../services/api/restApiAdapter';

const getAddressLabelIcon = (label) => {
  const l = (label || '').toLowerCase();
  if (l.includes('home')) return <Home size={14} />;
  if (l.includes('work') || l.includes('office')) return <Briefcase size={14} />;
  return <Building2 size={14} />;
};

const formatAddressDisplay = (addr) => {
  if (!addr) return '';
  if (addr.address) return addr.address;
  return [addr.addressLine1, addr.addressLine2, addr.landmark, addr.city, addr.postalCode]
    .filter(Boolean)
    .join(', ');
};

const DeliveryAddressSection = ({
  restaurantId,
  phone,
  onPhoneChange,
  recipientName,
  onRecipientNameChange,
  selectedAddress,
  onSelectAddress,
  theme,
}) => {
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [isLookupLoading, setIsLookupLoading] = useState(false);
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // New Address Form State
  const [newAddrLabel, setNewAddrLabel] = useState('Home');
  const [newAddrLine1, setNewAddrLine1] = useState('');
  const [newAddrLine2, setNewAddrLine2] = useState('');
  const [newLandmark, setNewLandmark] = useState('');
  const [newCity, setNewCity] = useState('');
  const [newPostalCode, setNewPostalCode] = useState('');
  const [newLatitude, setNewLatitude] = useState(null);
  const [newLongitude, setNewLongitude] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationStatus, setLocationStatus] = useState('');
  const [formError, setFormError] = useState('');

  // ── 1. Load cached addresses from localStorage on phone change ──────────────
  const loadLocalAddresses = useCallback((cleanPhone) => {
    if (!cleanPhone || cleanPhone.length < 10) return [];
    try {
      const key = `swado_addresses_${restaurantId}_${cleanPhone}`;
      const cached = localStorage.getItem(key);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Failed to parse cached addresses', e);
    }
    return [];
  }, [restaurantId]);

  // ── 2. Save addresses to localStorage ───────────────────────────────────────
  const persistLocalAddresses = useCallback((cleanPhone, addressList) => {
    if (!cleanPhone || cleanPhone.length < 10) return;
    try {
      const key = `swado_addresses_${restaurantId}_${cleanPhone}`;
      localStorage.setItem(key, JSON.stringify(addressList));
    } catch (e) {
      console.warn('Failed to persist addresses locally', e);
    }
  }, [restaurantId]);

  // ── 3. Debounced server lookup when phone has 10 digits ─────────────────────
  useEffect(() => {
    const cleanPhone = String(phone || '').replace(/\D/g, '').slice(-10);

    // Initial check from local storage (0ms latency)
    const local = loadLocalAddresses(cleanPhone);
    if (local.length > 0) {
      setSavedAddresses(local);
      if (!selectedAddress && local[0]) {
        onSelectAddress(local[0]);
      }
    }

    if (cleanPhone.length !== 10) {
      if (local.length === 0) setSavedAddresses([]);
      return;
    }

    let isSubscribed = true;
    const timer = setTimeout(async () => {
      setIsLookupLoading(true);
      try {
        const res = await lookupCustomerAddresses(restaurantId, cleanPhone);
        if (isSubscribed && res.success && res.data) {
          if (res.data.name && !recipientName) {
            onRecipientNameChange(res.data.name);
          }
          if (Array.isArray(res.data.addresses) && res.data.addresses.length > 0) {
            // Merge server addresses with local addresses (avoiding duplicates)
            const combined = [...res.data.addresses];
            local.forEach((loc) => {
              const locStr = formatAddressDisplay(loc).toLowerCase();
              if (!combined.some((srv) => formatAddressDisplay(srv).toLowerCase() === locStr)) {
                combined.push(loc);
              }
            });

            setSavedAddresses(combined);
            persistLocalAddresses(cleanPhone, combined);

            if (!selectedAddress && combined[0]) {
              onSelectAddress(combined[0]);
            }
          }
        }
      } catch (err) {
        console.error('Address lookup error:', err);
      } finally {
        if (isSubscribed) setIsLookupLoading(false);
      }
    }, 600);

    return () => {
      isSubscribed = false;
      clearTimeout(timer);
    };
  }, [phone, restaurantId, loadLocalAddresses, persistLocalAddresses]);

  // ── Trigger when user taps main address card or change button ──────────────
  const handleAddressCardClick = () => {
    if (savedAddresses.length > 0) {
      setIsSelectModalOpen(true);
    } else {
      setFormError('');
      setIsAddModalOpen(true);
    }
  };

  // ── GPS Geolocation with reverse geocode ────────────────────────────────────
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setFormError('Geolocation is not supported by your browser');
      return;
    }

    setIsLocating(true);
    setLocationStatus('Getting GPS coordinates...');
    setFormError('');

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setNewLatitude(lat);
        setNewLongitude(lng);
        setLocationStatus(`GPS: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);

        // Reverse geocoding via OpenStreetMap Nominatim
        try {
          setLocationStatus('Auto-filling address details...');
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`, {
            headers: { 'Accept-Language': 'en' },
          });
          if (res.ok) {
            const data = await res.json();
            const addr = data.address || {};
            if (addr.road || addr.suburb || addr.neighbourhood) {
              setNewAddrLine2((prev) => prev || addr.road || addr.neighbourhood || addr.suburb || '');
            }
            if (addr.city || addr.town || addr.village || addr.state_district) {
              setNewCity((prev) => prev || addr.city || addr.town || addr.village || addr.state_district || '');
            }
            if (addr.postcode) {
              setNewPostalCode((prev) => prev || addr.postcode.slice(0, 6));
            }
            setLocationStatus(`📍 Location & area detected`);
          }
        } catch (e) {
          console.warn('Reverse geocode error:', e);
          setLocationStatus(`📍 GPS coordinates captured`);
        } finally {
          setIsLocating(false);
        }
      },
      (err) => {
        setIsLocating(false);
        setLocationStatus('');
        console.warn('Geolocation error:', err);
        setFormError('Unable to get GPS location. Please allow location permissions or enter address manually.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  // ── Handle adding new address ───────────────────────────────────────────────
  const handleSaveNewAddress = (e) => {
    e.preventDefault();
    if (!newAddrLine1.trim()) {
      setFormError('House / Flat / Building number is required');
      return;
    }
    if (!newAddrLine2.trim()) {
      setFormError('Area / Street name is required');
      return;
    }
    if (!newCity.trim()) {
      setFormError('City is required');
      return;
    }

    const newAddr = {
      label: newAddrLabel || 'Home',
      addressLine1: newAddrLine1.trim(),
      addressLine2: newAddrLine2.trim(),
      landmark: newLandmark.trim() || undefined,
      city: newCity.trim(),
      postalCode: newPostalCode.trim() || undefined,
      latitude: newLatitude || undefined,
      longitude: newLongitude || undefined,
      lat: newLatitude || undefined,
      lng: newLongitude || undefined,
      address: [newAddrLine1.trim(), newAddrLine2.trim(), newLandmark.trim(), newCity.trim(), newPostalCode.trim()].filter(Boolean).join(', '),
    };

    const cleanPhone = String(phone || '').replace(/\D/g, '').slice(-10);
    const updated = [newAddr, ...savedAddresses.filter((a) => formatAddressDisplay(a) !== formatAddressDisplay(newAddr))];

    setSavedAddresses(updated);
    onSelectAddress(newAddr);
    persistLocalAddresses(cleanPhone, updated);

    // Reset and close modals
    setNewAddrLine1('');
    setNewAddrLine2('');
    setNewLandmark('');
    setNewCity('');
    setNewPostalCode('');
    setNewLatitude(null);
    setNewLongitude(null);
    setLocationStatus('');
    setFormError('');
    setIsAddModalOpen(false);
    setIsSelectModalOpen(false);
  };

  const handleDeleteAddress = (indexToDelete, e) => {
    e.stopPropagation();
    const updated = savedAddresses.filter((_, idx) => idx !== indexToDelete);
    setSavedAddresses(updated);
    const cleanPhone = String(phone || '').replace(/\D/g, '').slice(-10);
    persistLocalAddresses(cleanPhone, updated);
    if (selectedAddress === savedAddresses[indexToDelete]) {
      onSelectAddress(updated[0] || null);
    }
  };

  return (
    <div className="space-y-3">
      {/* ─── 1. Compact Recipient & Mobile Row ───────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">
            Recipient Name <span className="text-rose-500">*</span>
          </label>
          <div className="relative flex items-center">
            <User size={14} className="absolute left-3 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="e.g. Rahul Sharma"
              value={recipientName}
              onChange={(e) => onRecipientNameChange(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-gray-50/90 border border-gray-200/90 rounded-xl text-xs font-semibold text-gray-900 focus:bg-white focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all placeholder:text-gray-400"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500">
              Mobile Number <span className="text-rose-500">*</span>
            </label>
            {isLookupLoading && (
              <span className="inline-flex items-center gap-1 text-[9px] text-purple-600 font-bold">
                <Loader2 size={10} className="animate-spin" />
                Checking addresses...
              </span>
            )}
          </div>
          <div className="relative flex items-center">
            <span className="absolute left-3 text-[11px] font-bold text-gray-400 pointer-events-none">
              +91
            </span>
            <input
              type="tel"
              maxLength={10}
              placeholder="10-digit number"
              value={phone}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                onPhoneChange(val);
              }}
              className="w-full pl-10 pr-3 py-2 bg-gray-50/90 border border-gray-200/90 rounded-xl text-xs font-semibold text-gray-900 focus:bg-white focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all placeholder:text-gray-400 tracking-wider"
            />
          </div>
        </div>
      </div>

      {/* ─── 2. Compact Delivery Address Summary Tile ────────────────────────── */}
      <div>
        <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">
          Delivery Address <span className="text-rose-500">*</span>
        </label>

        {selectedAddress ? (
          /* Address Selected State - Compact Single Card */
          <div
            onClick={handleAddressCardClick}
            className="p-2.5 rounded-xl border border-purple-200/80 bg-purple-50/30 hover:bg-purple-50/60 transition-all cursor-pointer flex items-center justify-between gap-2.5 group"
          >
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <div className="w-7 h-7 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center shrink-0">
                {getAddressLabelIcon(selectedAddress.label)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-[10px] font-black uppercase tracking-wider text-purple-800">
                    {selectedAddress.label || 'Home'}
                  </span>
                  {selectedAddress.landmark && (
                    <span className="text-[10px] text-gray-400 truncate">
                      • Near {selectedAddress.landmark}
                    </span>
                  )}
                  {(selectedAddress.latitude || selectedAddress.lat) && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200/60">
                      <Crosshair size={9} /> GPS
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-700 font-medium truncate leading-tight">
                  {formatAddressDisplay(selectedAddress)}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleAddressCardClick();
              }}
              className="text-[11px] font-bold text-purple-700 bg-white hover:bg-purple-100/80 border border-purple-200 px-2.5 py-1 rounded-lg shrink-0 transition-all shadow-2xs cursor-pointer"
            >
              Change
            </button>
          </div>
        ) : (
          /* No Address Selected State - Sleek Action Tile */
          <div
            onClick={handleAddressCardClick}
            className="p-3 rounded-xl border border-dashed border-gray-300 hover:border-purple-400 bg-gray-50/60 hover:bg-purple-50/20 transition-all cursor-pointer flex items-center justify-between gap-2 group"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-7 h-7 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                <MapPin size={15} />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-800">Add delivery address</p>
                <p className="text-[10px] text-gray-400">House, street, and area details</p>
              </div>
            </div>

            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-purple-700 bg-purple-50 px-2.5 py-1 rounded-lg shrink-0">
              <Plus size={12} /> Add
            </span>
          </div>
        )}
      </div>

      {/* ─── 3. Bottom Sheet: Select Saved Address ───────────────────────────── */}
      <AnimatePresence>
        {isSelectModalOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-[70] backdrop-blur-xs"
              onClick={() => setIsSelectModalOpen(false)}
            />

            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 220 }}
              className="fixed bottom-0 left-0 right-0 max-w-lg mx-auto bg-white rounded-t-3xl z-[80] p-5 shadow-2xl flex flex-col max-h-[85vh] overflow-hidden"
            >
              {/* Sheet Header */}
              <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-3 shrink-0" />
              <div className="flex items-center justify-between pb-3 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                    <MapPin size={16} />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-gray-900">Select Delivery Address</h3>
                    <span className="text-[10px] text-gray-400">{savedAddresses.length} saved address{savedAddresses.length > 1 ? 'es' : ''}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsSelectModalOpen(false)}
                  className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 cursor-pointer"
                >
                  <X size={15} />
                </button>
              </div>

              {/* Saved Addresses List */}
              <div className="overflow-y-auto py-3 space-y-2 pr-1 flex-1">
                {savedAddresses.map((addr, idx) => {
                  const isSelected = selectedAddress && formatAddressDisplay(selectedAddress) === formatAddressDisplay(addr);
                  const label = addr.label || 'Home';

                  return (
                    <div
                      key={idx}
                      onClick={() => {
                        onSelectAddress(addr);
                        setIsSelectModalOpen(false);
                      }}
                      className={`p-3 rounded-xl border transition-all cursor-pointer flex items-start gap-3 relative ${
                        isSelected
                          ? 'bg-purple-50/40 border-purple-500/60 shadow-xs ring-1 ring-purple-500/20'
                          : 'bg-white border-gray-200/80 hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full mt-0.5 flex items-center justify-center shrink-0 transition-colors ${
                        isSelected ? 'bg-purple-600 text-white' : 'border border-gray-300 bg-white'
                      }`}>
                        {isSelected && <Check size={12} strokeWidth={3} />}
                      </div>

                      <div className="min-w-0 flex-1 pr-6">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${
                            isSelected ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {getAddressLabelIcon(label)}
                            <span>{label}</span>
                          </span>

                          {(addr.latitude || addr.lat) && (
                            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200/60">
                              <Crosshair size={9} /> GPS
                            </span>
                          )}
                        </div>

                        <p className="text-xs font-medium text-gray-800 line-clamp-2 leading-relaxed">
                          {formatAddressDisplay(addr)}
                        </p>

                        {addr.landmark && (
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            Landmark: {addr.landmark}
                          </p>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={(e) => handleDeleteAddress(idx, e)}
                        className="absolute top-2.5 right-2.5 p-1 text-gray-300 hover:text-rose-500 rounded-md hover:bg-rose-50 transition-colors cursor-pointer"
                        title="Remove address"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Bottom Action: Add New Address */}
              <div className="pt-2 border-t border-gray-100 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setFormError('');
                    setIsSelectModalOpen(false);
                    setIsAddModalOpen(true);
                  }}
                  className="w-full py-3 rounded-xl font-bold text-xs bg-purple-50 text-purple-700 border border-purple-200 flex items-center justify-center gap-2 hover:bg-purple-100/80 transition-all cursor-pointer"
                >
                  <Plus size={15} />
                  <span>Add New Address</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ─── 4. Bottom Sheet: Add New Address Form ───────────────────────────── */}
      <AnimatePresence>
        {isAddModalOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-[70] backdrop-blur-xs"
              onClick={() => setIsAddModalOpen(false)}
            />

            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 220 }}
              className="fixed bottom-0 left-0 right-0 max-w-lg mx-auto bg-white rounded-t-3xl z-[80] p-5 shadow-2xl flex flex-col max-h-[88vh] overflow-hidden"
            >
              {/* Sheet Header */}
              <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-3 shrink-0" />

              <div className="flex items-center justify-between pb-3 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-2">
                  {savedAddresses.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddModalOpen(false);
                        setIsSelectModalOpen(true);
                      }}
                      className="p-1 text-gray-500 hover:text-gray-800 rounded-lg hover:bg-gray-100 cursor-pointer"
                    >
                      <ArrowLeft size={16} />
                    </button>
                  )}
                  <div className="w-7 h-7 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                    <MapPin size={16} />
                  </div>
                  <h3 className="text-sm font-black text-gray-900">Add Delivery Address</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 cursor-pointer"
                >
                  <X size={15} />
                </button>
              </div>

              {/* Form Body */}
              <form onSubmit={handleSaveNewAddress} className="overflow-y-auto py-3 space-y-3.5 pr-1 flex-1">
                {formError && (
                  <div className="p-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold">
                    {formError}
                  </div>
                )}

                {/* ─── GPS Auto-Detect Button ──────────────────────────────── */}
                <div className="bg-purple-50/60 border border-purple-100 rounded-2xl p-3 flex items-center justify-between gap-2.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-xl bg-purple-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                      {isLocating ? <Loader2 size={16} className="animate-spin" /> : <Navigation size={16} />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-gray-900 leading-tight">
                        {newLatitude ? 'Location Attached' : 'Use Current Location'}
                      </p>
                      <p className="text-[10px] text-purple-700 font-medium truncate mt-0.5">
                        {locationStatus || (newLatitude ? `GPS: ${newLatitude.toFixed(4)}, ${newLongitude.toFixed(4)}` : 'Auto-detect coordinates via GPS')}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    disabled={isLocating}
                    onClick={handleUseCurrentLocation}
                    className="px-3 py-1.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold shrink-0 shadow-xs transition-all active:scale-95 disabled:opacity-50 cursor-pointer flex items-center gap-1"
                  >
                    <Crosshair size={12} />
                    <span>{newLatitude ? 'Update GPS' : 'Locate Me'}</span>
                  </button>
                </div>

                {/* Category Pills */}
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">
                    Save Address As
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {['Home', 'Work', 'Other'].map((lbl) => (
                      <button
                        key={lbl}
                        type="button"
                        onClick={() => setNewAddrLabel(lbl)}
                        className={`py-2 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 border transition-all cursor-pointer ${
                          newAddrLabel === lbl
                            ? 'bg-purple-600 text-white border-purple-600 shadow-xs'
                            : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {getAddressLabelIcon(lbl)}
                        <span>{lbl}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Line 1: House / Flat */}
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">
                    Flat / House No. / Building Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Flat 302, Palm Heights"
                    value={newAddrLine1}
                    onChange={(e) => setNewAddrLine1(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 font-medium focus:bg-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none placeholder:text-gray-400"
                  />
                </div>

                {/* Line 2: Street / Area */}
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">
                    Area / Street / Sector <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 5th Main Road, Indiranagar"
                    value={newAddrLine2}
                    onChange={(e) => setNewAddrLine2(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 font-medium focus:bg-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none placeholder:text-gray-400"
                  />
                </div>

                {/* Landmark */}
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">
                    Nearby Landmark (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Opposite City Park / Behind Metro"
                    value={newLandmark}
                    onChange={(e) => setNewLandmark(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 font-medium focus:bg-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none placeholder:text-gray-400"
                  />
                </div>

                {/* City & PIN Code */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">
                      City <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Bangalore"
                      value={newCity}
                      onChange={(e) => setNewCity(e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 font-medium focus:bg-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none placeholder:text-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">
                      PIN / Postal Code
                    </label>
                    <input
                      type="text"
                      maxLength={6}
                      placeholder="e.g. 560038"
                      value={newPostalCode}
                      onChange={(e) => setNewPostalCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 font-medium focus:bg-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none placeholder:text-gray-400"
                    />
                  </div>
                </div>

                {/* Manual Lat/Long display (if coordinates detected) */}
                {newLatitude && newLongitude && (
                  <div className="p-2.5 rounded-xl bg-gray-50 border border-gray-200/80 flex items-center justify-between text-[11px] text-gray-600">
                    <span className="flex items-center gap-1 font-semibold text-gray-700">
                      <Crosshair size={12} className="text-purple-600" />
                      Coordinates Attached:
                    </span>
                    <span className="font-mono text-xs font-bold text-gray-900">
                      {newLatitude.toFixed(6)}, {newLongitude.toFixed(6)}
                    </span>
                  </div>
                )}

                {/* Save Button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    className="w-full py-3 px-4 rounded-xl font-bold text-xs text-white shadow-md transition-all active:scale-98 flex items-center justify-center gap-2 cursor-pointer bg-purple-600 hover:bg-purple-700"
                  >
                    <Check size={15} />
                    <span>Save & Select Address</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DeliveryAddressSection;
